/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { FC, useContext, useEffect, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import moment from 'moment';
import { eventUtil } from '@marriott/mi-ui-library-shop';
import { Text, Types } from '@marriott/mi-ui-library';
import { usePersistentGlobalStore } from '@marriott/mi-store-utils';

import { phoenixOfferSuggestedPlacesQuery } from '@marriott/mi-offers-graphql';
import { ContentContext, FormValuesContext } from '../../../../OffersSearchForm/index.contexts';
import { CALENDAR_STATE, DESTINATION_STATE } from '../../../../OffersSearchForm/index.constants';
import { DATES, DESTINATION, DESTINATION_HIDDEN_FIELDS } from '../../../../OffersSearchForm/constants/StoreConstants';
import { UXL_ERROR_POLICY } from '../../../../../api/apiConstants';
import { setDestination, setDates } from '../../../../OffersSearchForm/store/store.actions';
import { getMPOFormattedDate, DateObjectType } from '../../../../OffersSearchForm/utils/DateUtils';
import { getUrlParam } from '../../../../OffersSearchForm/utils/OfferUtils';

import { SearchMobileProps } from './SearchMobile.types';
import SearchMobileModalLazy from './SearchMobileModalLazy';
import { StyledSearchMobile } from './SearchMobile.styles';

const SearchMobile: FC<SearchMobileProps> = ({
  className,
  isHideDestinationProps = false,
  isPublishUserSelection = false,
  isHideFlexibleDates = false,
  isMPO = false,
  isSPO = false,
  previewMode,
  acceptLanguage,
  colorScheme,
}) => {
  const { mobilePlaceholderText, dateFormat, requestId, acceptLang, title, dates, destination, offerId } =
    useContext(ContentContext);
  // const dateFormat = getDateFormat(acceptLanguage ?? '');

  // to get destination name and get it fixed on mobile view for SPO
  const { UUID, formValues, setFormValues } = useContext(FormValuesContext);
  const propertyName = formValues?.[DESTINATION]?.displayText;
  const globalData = usePersistentGlobalStore((state: any) => state.globalData);
  const ariesOffer = globalData?.sessionData?.cacheData?.data?.AriesOffer ?? {};
  // Check if the flag is already set in sessionStorage
  const isInitialDeeplinkLoad = window.sessionStorage.getItem('isInitialDeeplinkLoad') === null;
  const mpoOffersFlexibleDate = JSON.parse(window.sessionStorage.getItem('mpoOffersFlexibleDates') ?? 'false');
  const { fromDate, toDate } = formValues?.[DATES] ?? {};
  // to get the dates based on the localization
  const getLocalizedDate = (date: DateObjectType | undefined): string => {
    moment.locale('locale');
    if (date) {
      //initially date will be in the english format we need to change that to the localized value
      const formattedDate = moment(date).format();
      return moment(formattedDate).format(dateFormat);
    } else {
      return '';
    }
  };
  let displayTextDate = '';
  if (fromDate || toDate) {
    displayTextDate = `${getLocalizedDate(fromDate)}${toDate ? ` - ${getLocalizedDate(toDate)}` : ''}`;
  }
  //Update destination when offerId is same otherwise set with formvalues
  const destinationText =
    ariesOffer?.searchData?.offerId === offerId
      ? ariesOffer?.searchData?.address || formValues?.[DESTINATION]?.displayText
      : formValues?.[DESTINATION]?.displayText;
  const hiddenFieldsValues = formValues?.[DESTINATION_HIDDEN_FIELDS];
  const isServer = !(typeof window != 'undefined' && window.document);

  const childCount = formValues?.roomsAndGuests?.childrenCount;
  const numberInParty =
    (formValues?.roomsAndGuests?.adultsCount ? formValues.roomsAndGuests.adultsCount : 0) + (childCount ?? 0);
  const isUrlWithPage = !isServer ? new URLSearchParams(window?.location?.search)?.has('pg') : false;

  /* states */
  const [dialogOpenState, setDialogOpenState] = useState(false);
  const [searchState, setSearchState] = useState('');
  const [onloadInput, setOnloadInput] = useState<string>(isSPO ? propertyName || '' : '');
  const [recentPId, setRecentPid] = useState('');
  const [timerList, setTimerList] = useState<ReturnType<typeof setTimeout>[]>([]);

  //get onloadInput value
  useEffect(() => {
    const urlParamVal = getUrlParam();
    const inputValue = isSPO ? (urlParamVal !== '' ? urlParamVal : propertyName || '') : urlParamVal;
    setOnloadInput(inputValue);
  }, []);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const processSuggestionListdata = (suggestionListData: any) => {
    if (suggestionListData?.suggestedPlaces?.total > 0) {
      setRecentPid(suggestionListData.suggestedPlaces.edges[0]?.node?.placeId);
    }
  };

  const [getSuggestionList, { data: _suggestionListData, error: _suggestionListError }] = useLazyQuery(
    phoenixOfferSuggestedPlacesQuery,
    {
      fetchPolicy: isServer ? 'network-only' : 'cache-first',
      errorPolicy: UXL_ERROR_POLICY,
      context: {
        headers: {
          'x-request-id': requestId,
          'accept-language': acceptLang,
        },
      },
      onCompleted: processSuggestionListdata,
    }
  );

  const clearTimer = (timers: ReturnType<typeof setTimeout>[]): void => {
    if (timers.length) {
      for (let i = timers.length - 1; i >= 0; i--) {
        clearTimeout(timers[i]);
      }
      setTimerList([]);
    }
  };
  useEffect(() => {
    updateFormValues(onloadInput, recentPId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recentPId]);

  const updateFormValues = (val: string, pid?: string): void => {
    setFormValues(
      setDestination({
        displayText: val,
        pid,
        clusterCode: formValues?.destination?.clusterCode ?? '',
      })
    );
  };

  useEffect(() => {
    // Delaying API call till the input value finishes updating
    if (onloadInput) {
      clearTimer(timerList);
      const timer = setTimeout(() => {
        if (UUID) {
          // fetchSearchResults(onloadInput, UUID);
          getSuggestionList({
            variables: {
              query: onloadInput,
            },
          });
        }
      }, 500);
      setTimerList([...timerList, timer]);
    } else {
      // cancel current fetch timeout tiemr if user delete the input value
      clearTimer(timerList);
      // clear options list and show recently viewed when input value is cleared
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onloadInput]);

  useEffect(() => {
    const searchFormData = {
      latitude: hiddenFieldsValues?.latitude ?? '',
      longitude: hiddenFieldsValues?.longitude ?? '',
      stateCode: hiddenFieldsValues?.state ?? '',
      countryCode: hiddenFieldsValues?.country ?? '',
      startDate: getMPOFormattedDate(),
      endDate: getMPOFormattedDate(),
      quantity: formValues?.roomsAndGuests?.roomsCount,
      numberInParty: numberInParty,
      childAges: formValues?.roomsAndGuests?.childrenAges,
      flexible: formValues?.dates?.flexible === null ? null : 'on',
    };
    if (
      onloadInput &&
      (hiddenFieldsValues?.country || hiddenFieldsValues?.placeId) &&
      (!isUrlWithPage || (isUrlWithPage && !ariesOffer?.searchData?.address)) &&
      (ariesOffer?.searchData?.offerId !== offerId || !ariesOffer?.searchData?.address)
    ) {
      if (isInitialDeeplinkLoad) {
        setFormValues(
          setDates({
            ...(formValues?.[DATES] ?? {}),
            flexible: 'on',
          })
        );
        window.sessionStorage.setItem('mpoOffersFlexibleDates', 'true'); // On initial load, set mpoOffersFlexibleDates to true if it's a deeplink. This will be used by the PropertiesList component.

        // Set the flag so it won't be true on next reload
        window.sessionStorage.setItem('isInitialDeeplinkLoad', 'false');
      } else if (mpoOffersFlexibleDate) {
        setFormValues(
          setDates({
            ...(formValues?.[DATES] ?? {}),
            flexible: 'on',
          })
        );
        window.sessionStorage.setItem('mpoOffersFlexibleDates', 'true'); // On initial load, set mpoOffersFlexibleDates to true if it's a deeplink. This will be used by the PropertiesList component.
      }
      eventUtil.dispatch('DeepLinkUtil', { searchFormData });
      setOnloadInput(''); // remove onloadInput value to call DeepLinkUtil only once.
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formValues?.[DESTINATION_HIDDEN_FIELDS]?.country, formValues?.[DESTINATION_HIDDEN_FIELDS]?.placeId]);

  /* event handlers */
  // set form form state on dialog open
  const openDialog = (state: string): void => {
    setSearchState(state);
    setDialogOpenState(true);
  };

  return (
    <>
      <div className="mpo-search-title">
        {isMPO && <Text copyText={title} fontSize={Types.size.medium} element={Types.tags.paragraph} />}
      </div>
      <StyledSearchMobile className={`${className} container m-search-container ${previewMode ? 'preview-cta' : ''}`}>
        {!isHideDestinationProps && isMPO ? (
          <button className="search-primary-destination" onClick={(): void => openDialog(DESTINATION_STATE)}>
            {/* for sticky state, show only icon */}
            <span className="icon-location" />
            {/* for non sticky state, show icon and label */}
            <span className="t-font-xs icon-location">{destination}</span>
            <span className="t-subtitle-m">
              {' '}
              {
                // Showing right destination once selected destination when traversed pagination and comeback on page 1
                (onloadInput && !isUrlWithPage && !ariesOffer?.searchData?.address
                  ? onloadInput
                  : destinationText
                  ? destinationText
                  : mobilePlaceholderText) as string
              }
            </span>
          </button>
        ) : (
          !isHideDestinationProps &&
          !isMPO && (
            <button className="search-primary-destination">
              {/* for sticky state, show only icon */}
              <span className="icon-location" />
              {/* for non sticky state, show icon and label */}
              <span className="t-font-xs icon-location">{destination}</span>
              <span className="t-subtitle-m">{propertyName as string}</span>
            </button>
          )
        )}
        <button
          onClick={(): void => openDialog(CALENDAR_STATE)}
          className={`${isHideDestinationProps ? `no-padding` : ``}`}
        >
          {/* for sticky state, show only icon */}
          <Text copyText={''} customClass={'icon-nav---book'} fontSize={undefined} element={Types.tags.span} />
          {/* for non sticky state, show icon and label */}
          <Text
            copyText={dates}
            customClass={'t-font-xs icon-nav---book'}
            fontSize={undefined}
            element={Types.tags.span}
          />
          <Text
            copyText={displayTextDate}
            customClass={'t-subtitle-inverse-l search-date-label'}
            fontSize={undefined}
            element={Types.tags.span}
          />
        </button>
        {!dialogOpenState ? (
          ''
        ) : (
          <SearchMobileModalLazy
            searchState={searchState}
            setSearchState={setSearchState}
            setDialogOpenState={setDialogOpenState}
            dialogOpenState={dialogOpenState}
            isHideDestinationProps={isHideDestinationProps}
            isPublishUserSelection={isPublishUserSelection}
            isHideFlexibleDates={isHideFlexibleDates}
            isMPO={isMPO}
            isSPO={isSPO}
            acceptLanguage={acceptLanguage}
            colorScheme={colorScheme}
          />
        )}
      </StyledSearchMobile>
    </>
  );
};

export default SearchMobile;
